/**
 * CREATIVE DETONATOR - Unified Download Utility
 * Centralized download functions with CORS proxy support for R2 storage
 *
 * @version 1.0.0
 *
 * Features:
 * - Automatic R2 URL detection and proxy routing
 * - Blob-based downloads for proper file saving
 * - Fallback behavior for edge cases
 * - Video and image support
 */

import { API_CONFIG } from '../../features/generator/core/state.js';

// ============================================================================
// CONSTANTS
// ============================================================================

// R2 domains that require proxy
const R2_DOMAINS = [
  'r2.dev',
  'r2.cloudflarestorage.com'
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if URL is from R2 storage (requires proxy)
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export function isR2Url(url) {
  if (!url) return false;
  return R2_DOMAINS.some(domain => url.includes(domain));
}

/**
 * Get auth token for proxy requests
 * @returns {Promise<string|null>}
 */
async function getAuthToken() {
  try {
    // Try DashboardCore first (dashboard pages)
    if (window.DashboardCore?.Auth?.getToken) {
      return await window.DashboardCore.Auth.getToken();
    }
    // Try getSessionToken from auth module (generator page)
    const { getSessionToken } = await import('../../features/auth/js/client.js');
    return await getSessionToken();
  } catch (e) {
    console.warn('[Download] Could not get auth token:', e.message);
    return null;
  }
}

/**
 * Get file extension from URL or content type
 * @param {string} url - File URL
 * @param {string} contentType - MIME type (optional)
 * @returns {string}
 */
function getExtension(url, contentType) {
  // Try from content type first
  if (contentType) {
    if (contentType.includes('png')) return 'png';
    if (contentType.includes('jpeg') || contentType.includes('jpg')) return 'jpg';
    if (contentType.includes('gif')) return 'gif';
    if (contentType.includes('webp')) return 'webp';
    if (contentType.includes('mp4')) return 'mp4';
    if (contentType.includes('webm')) return 'webm';
  }

  // Try from URL
  if (url) {
    const match = url.match(/\.([a-z0-9]+)(?:\?|$)/i);
    if (match) return match[1].toLowerCase();
  }

  return 'png'; // Default
}

// ============================================================================
// MAIN DOWNLOAD FUNCTIONS
// ============================================================================

/**
 * Download an image file
 * Automatically uses proxy for R2 URLs to bypass CORS
 *
 * @param {string} imageUrl - URL of the image to download
 * @param {string} [filename] - Custom filename (auto-generated if not provided)
 * @returns {Promise<boolean>} - True if download succeeded
 */
export async function downloadImage(imageUrl, filename) {
  if (!imageUrl) {
    console.warn('[Download] No image URL provided');
    return false;
  }

  console.log('[Download] Starting image download:', imageUrl.substring(0, 80) + '...');

  try {
    let blob;
    let contentType;

    if (isR2Url(imageUrl)) {
      // Use backend proxy for R2 URLs
      console.log('[Download] Using proxy for R2 URL');
      const token = await getAuthToken();
      const proxyUrl = `${API_CONFIG.baseUrl}/api/ideas/media/download?url=${encodeURIComponent(imageUrl)}`;

      const response = await fetch(proxyUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error(`Proxy request failed: ${response.status}`);
      }

      blob = await response.blob();
      contentType = response.headers.get('content-type');
    } else {
      // Direct fetch for non-R2 URLs (Fal.ai, etc.)
      console.log('[Download] Direct fetch for non-R2 URL');
      const response = await fetch(imageUrl);

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status}`);
      }

      blob = await response.blob();
      contentType = response.headers.get('content-type');
    }

    // Generate filename if not provided
    const ext = getExtension(imageUrl, contentType);
    const finalFilename = filename || `creative-detonator-${Date.now()}.${ext}`;

    // Create blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    console.log('[Download] Image downloaded:', finalFilename);
    return true;

  } catch (error) {
    console.error('[Download] Image download failed:', error);

    // Fallback: try opening in new tab
    console.log('[Download] Falling back to window.open');
    window.open(imageUrl, '_blank');
    return false;
  }
}

/**
 * Download a video file
 * Automatically uses proxy for R2 URLs to bypass CORS
 *
 * @param {string} videoUrl - URL of the video to download
 * @param {string} [filename] - Custom filename (auto-generated if not provided)
 * @returns {Promise<boolean>} - True if download succeeded
 */
export async function downloadVideo(videoUrl, filename) {
  if (!videoUrl) {
    console.warn('[Download] No video URL provided');
    return false;
  }

  console.log('[Download] Starting video download:', videoUrl.substring(0, 80) + '...');

  try {
    let blob;
    let contentType;

    if (isR2Url(videoUrl)) {
      // Use backend proxy for R2 URLs
      console.log('[Download] Using proxy for R2 video URL');
      const token = await getAuthToken();
      const proxyUrl = `${API_CONFIG.baseUrl}/api/ideas/media/download?url=${encodeURIComponent(videoUrl)}`;

      const response = await fetch(proxyUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error(`Proxy request failed: ${response.status}`);
      }

      blob = await response.blob();
      contentType = response.headers.get('content-type');
    } else {
      // Direct fetch for non-R2 URLs
      console.log('[Download] Direct fetch for non-R2 video URL');
      const response = await fetch(videoUrl);

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status}`);
      }

      blob = await response.blob();
      contentType = response.headers.get('content-type');
    }

    // Generate filename if not provided
    const ext = getExtension(videoUrl, contentType) || 'mp4';
    const finalFilename = filename || `creative-detonator-video-${Date.now()}.${ext}`;

    // Create blob URL and trigger download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);

    console.log('[Download] Video downloaded:', finalFilename);
    return true;

  } catch (error) {
    console.error('[Download] Video download failed:', error);

    // Fallback: try opening in new tab
    console.log('[Download] Falling back to window.open');
    window.open(videoUrl, '_blank');
    return false;
  }
}

/**
 * Download a file (auto-detects type from URL)
 * @param {string} url - File URL
 * @param {string} [filename] - Custom filename
 * @returns {Promise<boolean>}
 */
export async function downloadFile(url, filename) {
  if (!url) return false;

  // Detect type from URL
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('.mp4') || lowerUrl.includes('.webm') || lowerUrl.includes('.mov')) {
    return downloadVideo(url, filename);
  }

  return downloadImage(url, filename);
}

// ============================================================================
// GLOBAL EXPORTS
// ============================================================================

// Expose globally for non-module usage
if (typeof window !== 'undefined') {
  window.downloadImage = downloadImage;
  window.downloadVideo = downloadVideo;
  window.downloadFile = downloadFile;
  window.isR2Url = isR2Url;
}
